import type { AppIcon } from "@repo/ui/components/icons";
import { buttonVariants } from "@repo/ui/components/ui/button";
import { fieldClass as sharedField } from "@repo/ui/components/ui/input";
import { cn } from "@repo/ui/lib/utils";
import type React from "react";

// The profile's card language (security panels, identity, email sign-in):
// white surface, 24px corners, an icon tile + title + one-line description,
// tall rounded fields and pill buttons. One place, so every panel matches;
// fields and buttons are the shared ones (F123), the field a little taller.

export const panelClass = "bg-background flex flex-col gap-4 rounded-card p-4 shadow-sm sm:p-5";
export const fieldClass = cn(sharedField, "h-12 text-base");
export const primaryButtonClass = buttonVariants();
export const secondaryButtonClass = buttonVariants({ variant: "outline" });

export function PanelHeader({ icon: Icon, title, description, badge, tone = "muted" }: { icon: AppIcon; title: string; description?: string; badge?: string; tone?: "muted" | "green" }) {
  return (
    <div className="flex items-start gap-3">
      <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl", tone === "green" ? "bg-tint-green text-ink-green" : "bg-surface text-muted-foreground")}>
        <Icon size={20} />
      </span>
      <div className="flex-1">
        <h2 className="flex items-center gap-2 font-bold">
          {title}
          {badge ? <span className="bg-tint-green text-ink-green rounded-full px-2 py-0.5 text-caption font-medium">{badge}</span> : null}
        </h2>
        {description ? <p className="text-muted-foreground text-xs leading-5">{description}</p> : null}
      </div>
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-muted-foreground flex items-center justify-between gap-2">
        {label}
        {hint}
      </span>
      {children}
    </label>
  );
}

/** The shared on/off switch (F174, packages/ui). */
export { Switch } from "@repo/ui/components/switch";
